import React, { useState, useEffect } from "react";
import SEO from "../seo";
const Plan = () => {
  const [activeSection, setActiveSection] = useState("overview");

  useEffect(() => {
    const handleScroll = () => {
      const sections = [
        "overview",
        "plan",
        "billing-cycle",
        "payment-method",
        "action-buttons",
      ];

      const scrollPosition = window.scrollY + 100;

      for (const sectionId of sections) {
        const element = document.getElementById(sectionId);
        if (element) {
          const offsetTop = element.offsetTop;
          const offsetHeight = element.offsetHeight;

          if (
            scrollPosition >= offsetTop &&
            scrollPosition < offsetTop + offsetHeight
          ) {
            setActiveSection(sectionId);
            break;
          }
        }
      }
    };

    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  return (
    <div className="flex flex-col lg:flex-row gap-6">
      <SEO
        title="Plan Settings | xPage Help Center"
        description="Learn how to manage your subscription on xPage, including plan details, billing cycles, payment methods, upgrading, downgrading, and deactivating your store."
        keywords="xPage plan settings, subscription management, billing cycle, payment method, upgrade plan, downgrade plan, deactivate store, xPage billing"
        canonical="https://help.xpage.ai/help/settings/Plan"
        type="article"
      />

      {/* Main Content */}
      <div className="flex-1 prose max-w-none">
        {/* Header Section */}
        <header id="overview" className="mb-6 scroll-mt-20">
          <h1 className="text-systemTitle text-2xl font-semibold mb-3">
            Plan Settings
          </h1>
          <p className="text-sm text-systemLabel">
            The Plan Settings section in Xpage provides full control over your
            subscription and billing configuration. This is where you can manage
            your current plan, review billing cycles, update payment methods,
            and take action to upgrade, change, or deactivate your store if
            necessary.
          </p>
          <p className="text-sm text-systemLabel">
            Keeping your plan information accurate ensures uninterrupted access
            to Xpage features and a smooth store operation.
          </p>
        </header>

        {/* Plan Section */}
        <section id="plan" className="mb-6 scroll-mt-20">
          <h2 className="text-sm font-semibold text-systemTitle mb-1">Plan</h2>

          <div className="bg-systemBackground border border-systemBorder rounded-lg p-4 mt-2">
            <div className="space-y-4">
              <div className="flex flex-col">
                <h3 className="text-sm font-semibold text-systemTitle mb-2">
                  Current Subscription
                </h3>
                <p className="text-sm text-systemLabel">
                  View your current Xpage subscription plan and the features it
                  includes.
                </p>
              </div>

              <div className="flex flex-col">
                <h3 className="text-sm font-semibold text-systemTitle mb-2">
                  Feature Understanding
                </h3>
                <p className="text-sm text-systemLabel">
                  Understand the capabilities available to you, from landing
                  page generation to advanced analytics and e-commerce
                  functionality.
                </p>
              </div>

              <div className="flex flex-col">
                <h3 className="text-sm font-semibold text-systemTitle mb-2">
                  Plan Comparison
                </h3>
                <p className="text-sm text-systemLabel">
                  Compare available plans to determine if upgrading or changing
                  your plan better supports your business growth goals.
                </p>
              </div>
            </div>
          </div>
        </section>

        {/* Billing Cycle Section */}
        <section id="billing-cycle" className="mb-6 scroll-mt-20">
          <h2 className="text-sm font-semibold text-systemTitle mb-1">
            Billing Cycle
          </h2>

          <div className="bg-systemBackground border border-systemBorder rounded-lg p-4 mt-2">
            <div className="space-y-4">
              <div className="flex flex-col">
                <h3 className="text-sm font-semibold text-systemTitle mb-2">
                  Billing Frequency
                </h3>
                <p className="text-sm text-systemLabel">
                  Review your billing frequency, whether monthly or annual.
                </p>
              </div>

              <div className="flex flex-col">
                <h3 className="text-sm font-semibold text-systemTitle mb-2">
                  Payment Schedule
                </h3>
                <p className="text-sm text-systemLabel">
                  Understand when your next payment is due and how your
                  subscription is billed.
                </p>
              </div>

              <div className="flex flex-col">
                <h3 className="text-sm font-semibold text-systemTitle mb-2">
                  Budget Alignment
                </h3>
                <p className="text-sm text-systemLabel">
                  Ensure your billing cycle aligns with your budgeting and
                  business planning requirements.
                </p>
              </div>
            </div>
          </div>
        </section>

        {/* Payment Method Section */}
        <section id="payment-method" className="mb-6 scroll-mt-20">
          <h2 className="text-sm font-semibold text-systemTitle mb-1">
            Payment Method
          </h2>

          <div className="bg-systemBackground border border-systemBorder rounded-lg p-4 mt-2">
            <div className="space-y-4">
              <div className="flex flex-col">
                <h3 className="text-sm font-semibold text-systemTitle mb-2">
                  Payment Management
                </h3>
                <p className="text-sm text-systemLabel">
                  Add or update the payment method used for your subscription.
                </p>
              </div>

              <div className="flex flex-col">
                <h3 className="text-sm font-semibold text-systemTitle mb-2">
                  Payment Options
                </h3>
                <p className="text-sm text-systemLabel">
                  Choose from supported payment options for seamless and secure
                  transactions.
                </p>
              </div>

              <div className="flex flex-col">
                <h3 className="text-sm font-semibold text-systemTitle mb-2">
                  Payment Security
                </h3>
                <p className="text-sm text-systemLabel">
                  Confirm that your payment information is up-to-date to avoid
                  service interruptions.
                </p>
              </div>
            </div>
          </div>
        </section>

        {/* Action Buttons Section */}
        <section id="action-buttons" className="scroll-mt-20">
          <h2 className="text-sm font-semibold text-systemTitle mb-1">
            Action Buttons
          </h2>

          <div className="bg-systemBackground border border-systemBorder rounded-lg p-4 mt-2">
            <div className="space-y-6">
              <div className="flex flex-col">
                <h3 className="text-sm font-semibold text-systemTitle mb-2">
                  Deactivate Store
                </h3>
                <p className="text-sm text-systemLabel">
                  Temporarily or permanently deactivate your store if needed.
                </p>
                <p className="text-sm text-systemLabel mt-2">
                  Xpage ensures a secure deactivation process while preserving
                  essential data for future reactivation.
                </p>
              </div>

              <div className="flex flex-col">
                <h3 className="text-sm font-semibold text-systemTitle mb-2">
                  Change Plan
                </h3>
                <p className="text-sm text-systemLabel">
                  Upgrade, downgrade, or switch your subscription plan.
                </p>
                <p className="text-sm text-systemLabel mt-2">
                  Choose a plan that best fits your current business needs,
                  growth goals, and desired feature set.
                </p>
              </div>
            </div>

            <div className="bg-system200 border-l-4 border-system400 px-4 py-3 rounded-r-md mt-4">
              <p className="text-sm text-systemLabel">
                By managing your plan effectively, you maintain full control
                over your Xpage subscription, ensuring access to the tools and
                features necessary to grow and operate your store without
                interruptions.
              </p>
            </div>
          </div>
        </section>
      </div>

      {/* Table of Contents Sidebar */}
      <div className="lg:w-64 flex-shrink-0 lg:block hidden">
        <div className="sticky top-6 bg-systemBackground border border-systemBorder rounded-lg p-4">
          <h3 className="text-systemTitle text-sm font-semibold mb-3">
            On this page
          </h3>
          <ul className="text-systemLabel text-sm space-y-2">
            <li>
              <a
                href="#overview"
                className={`block py-1 transition-colors border-l-2 pl-3 ${
                  activeSection === "overview"
                    ? "text-deepPurple font-semibold border-deepPurple"
                    : "border-transparent hover:text-deepPurple"
                }`}
              >
                Overview
              </a>
            </li>
            <li>
              <a
                href="#plan"
                className={`block py-1 transition-colors border-l-2 pl-3 ${
                  activeSection === "plan"
                    ? "text-deepPurple font-semibold border-deepPurple"
                    : "border-transparent hover:text-deepPurple"
                }`}
              >
                Plan
              </a>
            </li>
            <li>
              <a
                href="#billing-cycle"
                className={`block py-1 transition-colors border-l-2 pl-3 ${
                  activeSection === "billing-cycle"
                    ? "text-deepPurple font-semibold border-deepPurple"
                    : "border-transparent hover:text-deepPurple"
                }`}
              >
                Billing Cycle
              </a>
            </li>
            <li>
              <a
                href="#payment-method"
                className={`block py-1 transition-colors border-l-2 pl-3 ${
                  activeSection === "payment-method"
                    ? "text-deepPurple font-semibold border-deepPurple"
                    : "border-transparent hover:text-deepPurple"
                }`}
              >
                Payment Method
              </a>
            </li>
            <li>
              <a
                href="#action-buttons"
                className={`block py-1 transition-colors border-l-2 pl-3 ${
                  activeSection === "action-buttons"
                    ? "text-deepPurple font-semibold border-deepPurple"
                    : "border-transparent hover:text-deepPurple"
                }`}
              >
                Action Buttons
              </a>
            </li>
          </ul>
        </div>
      </div>
    </div>
  );
};

export default Plan;
